import { createFileRoute } from "@tanstack/react-router";
import { motion } from "motion/react";
import { Clock, FileCheck2, Globe2, ShieldQuestion } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { articleSchema, breadcrumbSchema, faqPageSchema, localizedHead, medicalWebPageSchema, validateLangSearch } from "@/lib/seo";
import { RelatedLinks } from "@/components/related-links";
import { ConsultCta } from "@/components/consult-cta";

const approvalFaqs: Record<"de" | "en", { q: string; a: string }[]> = {
  de: [
    { q: "Ist Retatrutid schon zugelassen?", a: "Nein. Retatrutid (LY3437943) befindet sich in Phase III (TRIUMPH-Programm) und ist als Fertigarzneimittel weder in der EU noch in den USA zugelassen. Zugang besteht ausschließlich über genehmigte klinische Studienprogramme." },
    { q: "Wann kommt die Zulassung von Retatrutid?", a: "Eli Lilly plant die Einreichung bei FDA und EMA für 2026 nach Abschluss der TRIUMPH-1-Studie. Eine reguläre Marktzulassung in den USA und der EU ist frühestens 2027 realistisch." },
    { q: "Was ist TRIUMPH-1?", a: "TRIUMPH-1 ist die zentrale Phase-III-Zulassungsstudie: rund 2.100 Erwachsene mit Adipositas werden über 80 Wochen gegen Placebo behandelt. Der Studienabschluss liefert die Datengrundlage für den Zulassungsantrag bei FDA und EMA." },
    { q: "Ist Retatrutid in Deutschland verschreibungsfähig?", a: "Nein. Beim BfArM gibt es kein Fertigarzneimittel im Handel. Eine Verordnung ist nur im Rahmen genehmigter klinischer Studien möglich." },
    { q: "Was hat die EMA bisher zu Retatrutid entschieden?", a: "Die EMA listet Retatrutid bislang nur im Kontext eines pädiatrischen Prüfplans (PIP). Eine Zulassung als Adipositas-Medikament folgt voraussichtlich nach der FDA-Entscheidung." },
  ],
  en: [
    { q: "Is Retatrutide already approved?", a: "No. Retatrutide (LY3437943) is in Phase III (TRIUMPH program) and not approved as a finished pharmaceutical in either the EU or the US. Access is only available through approved clinical study programs." },
    { q: "When will Retatrutide be approved?", a: "Eli Lilly plans regulatory submission to the FDA and EMA in 2026, after completion of TRIUMPH-1. Regular market approval in the US and EU is realistic at the earliest in 2027." },
    { q: "What is TRIUMPH-1?", a: "TRIUMPH-1 is the pivotal Phase III registration trial: approximately 2,100 adults with obesity are treated for 80 weeks versus placebo. Trial completion provides the data basis for the FDA and EMA submissions." },
    { q: "Can Retatrutide be prescribed in Germany?", a: "No. There is no finished pharmaceutical on the German market via BfArM. Prescription is only possible within approved clinical studies." },
    { q: "What has the EMA decided about Retatrutide so far?", a: "The EMA to date lists Retatrutide only in the context of a paediatric investigation plan (PIP). Approval as an obesity medicine is expected to follow the FDA decision." },
  ],
};

export const Route = createFileRoute("/zulassung")({
  validateSearch: validateLangSearch,
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: ({ deps }) => ({ lang: deps.lang }),
  head: ({ loaderData }) => {
    const lang = loaderData?.lang ?? "en";
    return {
      ...localizedHead("/zulassung", loaderData?.lang),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema("/zulassung", loaderData?.lang)) },
        { type: "application/ld+json", children: JSON.stringify(medicalWebPageSchema("/zulassung", loaderData?.lang, { specialty: "Pharmacologic", aspect: "Overview" })) },
        { type: "application/ld+json", children: JSON.stringify(faqPageSchema("/zulassung", loaderData?.lang, approvalFaqs[lang])) },
        { type: "application/ld+json", children: JSON.stringify(articleSchema("/zulassung", loaderData?.lang, { section: "Regulatory" })) },
      ],
    };
  },
  component: Page,
});



function Page() {
  const { lang } = useI18n();
  const de = lang === "de";

  const c = de
    ? {
        eyebrow: "Regulatorik",
        h1: "Zulassung von Retatrutid – Status 2026",
        lead: "Retatrutid (LY3437943) ist als Fertigarzneimittel für den freien Verkauf noch in der Zulassungsphase und daher ausschließlich über genehmigte klinische Studienprogramme – wie das unserer Partner-Klinik – zugänglich. Der Wirkstoff befindet sich in der Phase-III-Prüfung (TRIUMPH-Programm). Übersicht über den aktuellen Stand bei FDA, EMA und in Deutschland.",
        timeline: [
          { year: "2022", title: "Phase-II-Daten publiziert", body: "NEJM: bis zu 24 % Gewichtsverlust nach 48 Wochen bei 12 mg wöchentlich." },
          { year: "2023", title: "TRIUMPH-Phase-III-Programm startet", body: "TRIUMPH-1 bis TRIUMPH-4 rekrutieren mehrere zehntausend Teilnehmer weltweit." },
          { year: "2025", title: "Erste Phase-III-Ergebnisse", body: "Zwischenauswertungen bestätigen Wirksamkeit und Sicherheitsprofil." },
          { year: "2026", title: "Erwarteter Zulassungsantrag", body: "Eli Lilly plant Einreichung bei FDA und EMA nach Abschluss von TRIUMPH-1." },
          { year: "2027", title: "Mögliche Marktzulassung", body: "Frühester realistischer Zeitpunkt für eine reguläre Zulassung in USA und EU." },
        ],
        agencies: [
          { icon: FileCheck2, name: "FDA (USA)", body: "Prüfungsprozess läuft im Rahmen der Phase-III-Studien. Kein Zulassungsantrag vor 2026." },
          { icon: Globe2, name: "EMA (EU)", body: "EMA listet Retatrutid bislang nur im Kontext eines pädiatrischen Prüfplans. Zulassung als Adipositas-Medikament folgt nach FDA-Entscheidung." },
          { icon: ShieldQuestion, name: "Deutschland (BfArM)", body: "Kein Fertigarzneimittel im Handel. Verordnung ist im Rahmen genehmigter klinischer Studien möglich." },
        ],
        triumphTitle: "TRIUMPH-1 – die entscheidende Studie",
        triumphBody:
          "TRIUMPH-1 ist die zentrale Phase-III-Zulassungsstudie: rund 2.100 Erwachsene mit Adipositas, 80 Wochen Behandlungsdauer, Vergleich mit Placebo. Der Studienabschluss liefert die Datengrundlage für den Zulassungsantrag bei FDA und EMA.",
        note: "Hinweis:",
        noteBody:
          "Alle Zulassungstermine sind Prognosen auf Basis aktueller Kommunikation von Eli Lilly und den Studienregistrierungen. Verzögerungen sind möglich.",
      }
    : {
        eyebrow: "Regulatory",
        h1: "Retatrutide approval – status 2026",
        lead: "Retatrutide (LY3437943) is still in the regulatory approval phase as a commercial finished medicine, but is accessible through approved clinical study programs such as our partner clinic's. The compound is in Phase III (TRIUMPH program). Overview of the current status at FDA, EMA and in Germany.",
        timeline: [
          { year: "2022", title: "Phase II data published", body: "NEJM: up to 24% weight loss after 48 weeks at 12 mg weekly." },
          { year: "2023", title: "TRIUMPH Phase III program starts", body: "TRIUMPH-1 through TRIUMPH-4 enroll tens of thousands of participants worldwide." },
          { year: "2025", title: "First Phase III readouts", body: "Interim analyses confirm efficacy and safety profile." },
          { year: "2026", title: "Expected regulatory submission", body: "Eli Lilly plans submission to FDA and EMA after completion of TRIUMPH-1." },
          { year: "2027", title: "Possible market approval", body: "Earliest realistic timing for regular approval in the US and EU." },
        ],
        agencies: [
          { icon: FileCheck2, name: "FDA (USA)", body: "Review process ongoing within Phase III trials. No regulatory submission before 2026." },
          { icon: Globe2, name: "EMA (EU)", body: "EMA to date lists Retatrutide only in the context of a pediatric investigation plan. Obesity approval follows the FDA decision." },
          { icon: ShieldQuestion, name: "Germany (BfArM)", body: "No finished pharmaceutical on the market. Prescription is possible within approved clinical studies." },
        ],
        triumphTitle: "TRIUMPH-1 – the pivotal trial",
        triumphBody:
          "TRIUMPH-1 is the pivotal Phase III registration trial: approximately 2,100 adults with obesity, 80-week treatment duration, versus placebo. Trial completion provides the data basis for the FDA and EMA submission.",
        note: "Note:",
        noteBody:
          "All approval dates are projections based on current Eli Lilly communication and trial registrations. Delays are possible.",
      };

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 md:py-20">
      <div className="max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo">
          {c.eyebrow}
        </p>
        <h1 className="mt-3 font-display text-4xl leading-tight text-foreground md:text-5xl">
          {c.h1}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">{c.lead}</p>
      </div>

      <div className="mt-12 grid gap-4 md:grid-cols-3">
        {c.agencies.map(({ icon: Icon, name, body }) => (
          <motion.div
            key={name}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="card-elevated p-5"
          >
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-indigo/15 text-indigo">
              <Icon className="h-5 w-5" />
            </span>
            <h2 className="mt-4 text-lg font-semibold text-foreground">{name}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{body}</p>
          </motion.div>
        ))}
      </div>

      <section className="mt-16">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-indigo">
          <Clock className="h-3.5 w-3.5" />
          Timeline
        </div>
        <ol className="mt-6 space-y-4 border-l border-border pl-6">
          {c.timeline.map((step, i) => (
            <motion.li
              key={step.year}
              initial={{ opacity: 0, x: -8 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className="relative"
            >
              <span className="absolute -left-[31px] top-1.5 grid h-4 w-4 place-items-center rounded-full border border-border bg-background">
                <span className="h-1.5 w-1.5 rounded-full bg-indigo" />
              </span>
              <div className="flex flex-wrap items-baseline gap-x-3">
                <span className="font-mono text-sm font-semibold text-indigo">{step.year}</span>
                <h3 className="text-base font-semibold text-foreground">{step.title}</h3>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{step.body}</p>
            </motion.li>
          ))}
        </ol>
      </section>

      <section className="mt-16 rounded-2xl border border-border bg-card p-6 md:p-10">
        <h2 className="font-display text-2xl text-foreground md:text-3xl">{c.triumphTitle}</h2>
        <p className="mt-3 text-muted-foreground">{c.triumphBody}</p>
      </section>

      <p className="mt-10 text-xs text-muted-foreground">
        <strong className="text-foreground">{c.note}</strong> {c.noteBody}
      </p>

      <ConsultCta variant="section" />
      <RelatedLinks only={["/studien", "/vergleich", "/sicherheit"]} />
    </div>
  );
}
